import React, { useState, useEffect, useMemo } from "react";
import apiClient from "../utils/apiClient";
import { Search, RefreshCw } from "lucide-react";

export function RmsArbOrdersView() {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState("ALL");

    const fetchOrders = async () => {
        setLoading(true);
        try {
            const res = await apiClient.get("rms-arb/orders");
            if (res.data && res.data.status === "success") {
                setOrders(res.data.data || []);
            }
        } catch (err) {
            console.error("Error fetching basket orders:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOrders();
    }, []);

    const filteredOrders = useMemo(() => {
        return orders.filter((o) => {
            if (statusFilter !== "ALL") {
                const status = (o.status || "").toUpperCase();
                if (statusFilter === "VIRTUAL" && !o.is_virtual) return false;
                if (statusFilter === "EXECUTED" && status !== "EXECUTED" && status !== "FILLED" && status !== "CLOSED") return false;
                if (statusFilter === "OPEN" && status !== "OPEN" && status !== "PENDING") return false;
                if (statusFilter === "REJECTED" && status !== "REJECTED" && status !== "FAILED") return false;
            }

            if (searchQuery.trim()) {
                const q = searchQuery.toLowerCase();
                const symbol = (o.symbol || "").toLowerCase();
                const basketId = (o.basket_id || "").toLowerCase();
                const legId = (o.leg_id || "").toLowerCase();
                const clientId = (o.client_id || "").toLowerCase();
                return symbol.includes(q) || basketId.includes(q) || legId.includes(q) || clientId.includes(q);
            }
            return true;
        });
    }, [orders, statusFilter, searchQuery]);

    const stats = useMemo(() => {
        let total = orders.length;
        let executed = 0;
        let virtual = 0;
        let rejected = 0;

        orders.forEach((o) => {
            if (o.is_virtual) virtual++;
            const s = (o.status || "").toUpperCase();
            if (s === "EXECUTED" || s === "FILLED" || s === "CLOSED") executed++;
            if (s === "REJECTED" || s === "FAILED") rejected++;
        });

        return { total, executed, virtual, rejected };
    }, [orders]);

    return (
        <div className="p-4 md:p-6 space-y-6 bg-[#0f1116] min-h-screen text-gray-200">
            {/* Top Bar / Summary Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="bg-[#1a1d29] border border-gray-800 rounded-xl p-4 shadow-md">
                    <span className="text-xs text-gray-400 font-semibold uppercase">Total Leg Orders</span>
                    <div className="text-2xl font-extrabold text-white mt-1 tabular-nums">{stats.total}</div>
                </div>
                <div className="bg-[#1a1d29] border border-gray-800 rounded-xl p-4 shadow-md">
                    <span className="text-xs text-emerald-400 font-semibold uppercase">Executed</span>
                    <div className="text-2xl font-extrabold text-emerald-400 mt-1 tabular-nums">{stats.executed}</div>
                </div>
                <div className="bg-[#1a1d29] border border-gray-800 rounded-xl p-4 shadow-md">
                    <span className="text-xs text-amber-400 font-semibold uppercase">Virtual Legs</span>
                    <div className="text-2xl font-extrabold text-amber-400 mt-1 tabular-nums">{stats.virtual}</div>
                </div>
                <div className="bg-[#1a1d29] border border-gray-800 rounded-xl p-4 shadow-md">
                    <span className="text-xs text-red-400 font-semibold uppercase">Rejected</span>
                    <div className="text-2xl font-extrabold text-red-400 mt-1 tabular-nums">{stats.rejected}</div>
                </div>
            </div>

            {/* Filter & Search Bar */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-[#1a1d29] border border-gray-800 rounded-xl p-4 shadow-md">
                <div className="flex items-center gap-3 w-full sm:w-auto flex-1 max-w-md">
                    <div className="relative w-full">
                        <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                        <input
                            type="text"
                            placeholder="Search by Symbol, Basket ID, Client..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full bg-[#11131c] border border-gray-700 rounded-lg pl-9 pr-3 py-2 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
                        />
                    </div>
                </div>

                <div className="flex items-center gap-3 w-full sm:w-auto justify-end">
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="bg-[#11131c] border border-gray-700 rounded-lg px-3 py-2 text-xs text-white focus:outline-none focus:border-blue-500"
                    >
                        <option value="ALL">All Statuses</option>
                        <option value="EXECUTED">Executed</option>
                        <option value="OPEN">Open / Pending</option>
                        <option value="VIRTUAL">Virtual</option>
                        <option value="REJECTED">Rejected</option>
                    </select>

                    <button
                        onClick={fetchOrders}
                        className="flex items-center gap-1.5 px-3 py-2 bg-gray-800 hover:bg-gray-700 text-gray-300 border border-gray-700 rounded-lg text-xs font-semibold transition-all"
                    >
                        <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
                        <span>Refresh</span>
                    </button>
                </div>
            </div>

            {/* Table */}
            <div className="bg-[#1a1d29] border border-gray-800 rounded-xl overflow-hidden shadow-lg">
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-xs border-collapse">
                        <thead>
                            <tr className="bg-[#141722] border-b border-gray-800 text-gray-400 font-semibold uppercase tracking-wider">
                                <th className="py-3 px-4">Timestamp</th>
                                <th className="py-3 px-4">Basket ID</th>
                                <th className="py-3 px-4">Strategy</th>
                                <th className="py-3 px-4">Symbol</th>
                                <th className="py-3 px-4">Side</th>
                                <th className="py-3 px-4">Lots</th>
                                <th className="py-3 px-4">Limit Price</th>
                                <th className="py-3 px-4">Entry Price</th>
                                <th className="py-3 px-4">Broker / Client</th>
                                <th className="py-3 px-4 text-center">Execution Status</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-800/60 text-gray-200 font-mono">
                            {loading && filteredOrders.length === 0 ? (
                                <tr>
                                    <td colSpan="10" className="py-8 text-center text-gray-400">
                                        <div className="flex items-center justify-center gap-2">
                                            <RefreshCw className="w-4 h-4 animate-spin text-blue-400" />
                                            <span>Loading orders history...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : filteredOrders.length === 0 ? (
                                <tr>
                                    <td colSpan="10" className="py-8 text-center text-gray-400 font-sans">
                                        No basket leg execution history found matching criteria.
                                    </td>
                                </tr>
                            ) : (
                                filteredOrders.map((o, idx) => {
                                    const isBuy = Number(o.side) === 1;
                                    const statusStr = (o.status || "OPEN").toUpperCase();
                                    const createdAtStr = o.created_at
                                        ? new Date(o.created_at).toLocaleString("en-IN", {
                                            day: "2-digit",
                                            month: "2-digit",
                                            year: "numeric",
                                            hour: "2-digit",
                                            minute: "2-digit",
                                            second: "2-digit",
                                            hour12: false
                                          })
                                        : "-";

                                    return (
                                        <tr key={o.leg_id || idx} className="hover:bg-gray-800/30 transition-colors">
                                            <td className="py-3 px-4 text-gray-400">{createdAtStr}</td>
                                            <td className="py-3 px-4 font-bold text-white">{o.basket_id}</td>
                                            <td className="py-3 px-4 text-gray-400 uppercase text-[11px]">
                                                {(o.strategy_type || "arb").replace(/_/g, " ")}
                                            </td>
                                            <td className="py-3 px-4 font-bold text-blue-300">{o.symbol}</td>
                                            <td className="py-3 px-4">
                                                <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                                                    isBuy ? "bg-blue-500/20 text-blue-400 border border-blue-500/30" : "bg-red-500/20 text-red-400 border border-red-500/30"
                                                }`}>
                                                    {isBuy ? "BUY" : "SELL"}
                                                </span>
                                            </td>
                                            <td className="py-3 px-4">{o.qty}</td>
                                            <td className="py-3 px-4">{o.limit_price || "-"}</td>
                                            <td className="py-3 px-4 font-semibold text-emerald-400">{o.entry_price || o.limit_price || "-"}</td>
                                            <td className="py-3 px-4 text-gray-300">
                                                {o.broker ? `${o.broker.toUpperCase()} (${o.client_id})` : o.client_id}
                                            </td>
                                            <td className="py-3 px-4 text-center">
                                                {o.is_virtual ? (
                                                    <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-amber-500/20 text-amber-400 border border-amber-500/30">
                                                        VIRTUAL EXEC
                                                    </span>
                                                ) : statusStr === "EXECUTED" || statusStr === "FILLED" || statusStr === "CLOSED" || statusStr === "OPEN" ? (
                                                    <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                                                        {statusStr}
                                                    </span>
                                                ) : (
                                                    <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-red-500/20 text-red-400 border border-red-500/30">
                                                        {statusStr}
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
